/**
 * TestMondayEmailButton — Command Centre one-click Pulse email tester.
 * Jul 2026
 *
 * Fires POST /api/admin/weekly-pulse/send-test which hands the exact
 * Monday-morning HiBFF Pulse HTML to Postmark, addressed to the
 * currently-signed-in admin (bypasses the once-per-week dedup so it
 * can be re-tested as often as needed).
 *
 * Handy when you want to eyeball the full email — including the new
 * Pytest Flake Health section — without waiting until Monday 08:00 UTC
 * or dropping into DevTools to curl the endpoint manually.
 */
import React, { useState } from 'react';
import { Send, CheckCircle2, AlertTriangle } from 'lucide-react';
import { apiClient } from '../../api/client';

const NAVY = '#0F2A55';

export const TestMondayEmailButton = () => {
  const [state, setState] = useState('idle'); // idle | sending | sent | error
  const [target, setTarget] = useState(null);
  const [errMsg, setErrMsg] = useState('');

  const send = async () => {
    setState('sending');
    setErrMsg('');
    try {
      const res = await apiClient.request('/api/admin/weekly-pulse/send-test', { method: 'POST' });
      // Backend returns { status: "sent", to, postmark_result } regardless of
      // whether Postmark actually delivered — the Postmark error (if any)
      // lands inside `postmark_result.error`. Surface that as a real failure
      // so the founder knows the email didn't leave the pod.
      const pmError = res?.postmark_result?.error;
      if (pmError) {
        setErrMsg(pmError.length > 120 ? `${pmError.slice(0, 117)}…` : pmError);
        setState('error');
        setTimeout(() => setState('idle'), 12000);
        return;
      }
      setTarget(res?.to || 'your inbox');
      setState('sent');
      // Reset to idle after 6 seconds so the founder can fire again if they tweak content.
      setTimeout(() => setState('idle'), 6000);
    } catch (e) {
      setErrMsg(e?.message || 'Send failed');
      setState('error');
      setTimeout(() => setState('idle'), 8000);
    }
  };

  const isSending = state === 'sending';
  const isSent = state === 'sent';
  const isError = state === 'error';

  const accent = isSent ? '#2EAA5C' : isError ? '#DC2626' : '#7C3AED';
  const bg = isSent ? '#F0FAF3' : isError ? '#FEF2F2' : '#FFFFFF';

  return (
    <div
      data-testid="test-monday-email-card"
      className="p-3 rounded-lg flex items-center justify-between gap-3"
      style={{ backgroundColor: bg, border: `2px solid ${NAVY}` }}
    >
      <div className="flex items-center gap-3 min-w-0 flex-1">
        <div
          className="w-10 h-10 rounded-lg flex items-center justify-center shrink-0"
          style={{ backgroundColor: `${accent}20` }}
        >
          {isSent ? (
            <CheckCircle2 size={20} style={{ color: accent }} />
          ) : isError ? (
            <AlertTriangle size={20} style={{ color: accent }} />
          ) : (
            <Send size={20} style={{ color: accent }} />
          )}
        </div>
        <div className="min-w-0 flex-1">
          <div
            data-testid="test-monday-email-headline"
            className="text-sm font-bold"
            style={{ color: '#1A1209' }}
          >
            {isSent && `Monday Pulse sent to ${target}`}
            {isError && 'Send failed'}
            {isSending && 'Sending Monday Pulse…'}
            {state === 'idle' && 'Preview the Monday Pulse email'}
          </div>
          <div className="text-xs mt-0.5" style={{ color: '#44403A' }}>
            {isError
              ? errMsg
              : 'Includes the full HiBFF Pulse — Components, Signup Security, Magic-Link Health, Pytest Flakes.'}
          </div>
        </div>
      </div>

      <button
        data-testid="test-monday-email-send"
        onClick={send}
        disabled={isSending}
        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded text-xs font-semibold transition-colors disabled:opacity-60"
        style={{ backgroundColor: accent, color: 'white' }}
        title="Send the exact Monday-morning HiBFF Pulse email to your own inbox"
      >
        <Send size={14} className={isSending ? 'animate-pulse' : ''} />
        {isSending ? 'Sending…' : isSent ? 'Sent' : 'Send test'}
      </button>
    </div>
  );
};

export default TestMondayEmailButton;
